/**
 * 花瓣欠费平台服务
 * 文档: 花瓣欠费组件 H5 版本
 */

import { arrearsLogger } from './arrearsLogger'
import { arrearsCdnUrl, arrearsAppId } from '../../config/arrearsConfig'

// 花瓣欠费插件 CDN 地址
const ARREARS_PLUGIN_URL = arrearsCdnUrl

// 车牌颜色枚举
export const LICENSE_CAR_COLOR = {
  BLUE: 1, // 蓝色（默认）
  YELLOW: 2, // 黄色
  GREEN: 3, // 绿色
  BLACK: 4, // 黑色
  WHITE: 5, // 白色
  BLUE_WHITE_GRADIENT: 6, // 蓝白渐变牌
  YELLOW_GREEN_DUAL: 7, // 黄绿双拼牌
  OTHER: 0 // 其他
}

// 插件实例
let arrearsPlugin = null
let pluginLoaded = false

/**
 * 动态加载花瓣欠费插件
 * @returns {Promise<boolean>}
 */
export function loadArrearsPlugin () {
  return new Promise((resolve, reject) => {
    if (pluginLoaded && window.ArrearsPluginH5) {
      arrearsLogger.info('插件已加载，跳过重复加载')
      resolve(true)
      return
    }

    // 检查是否已存在
    if (window.ArrearsPluginH5) {
      pluginLoaded = true
      arrearsLogger.info('插件已存在于 window，标记为已加载')
      resolve(true)
      return
    }

    arrearsLogger.info('开始加载插件 CDN:', ARREARS_PLUGIN_URL)

    const script = document.createElement('script')
    script.src = ARREARS_PLUGIN_URL
    script.async = true

    script.onload = () => {
      arrearsLogger.info('插件 CDN 加载成功')
      pluginLoaded = true
      resolve(true)
    }

    script.onerror = (err) => {
      arrearsLogger.error('插件 CDN 加载失败', err)
      reject(err)
    }

    document.head.appendChild(script)
  })
}

/**
 * 初始化花瓣欠费组件
 * @param {Object} config 配置参数
 * @param {string} config.appId 应用ID
 * @param {string} config.openId 用户OpenId
 * @param {string} config.parkingId 停车场ID
 * @param {boolean} config.isPaid 是否开通追缴功能
 * @param {boolean} config.isDebug 是否开启调试模式
 * @param {string} config.callbackURL 回跳页面URL
 * @returns {Promise<Object>} 插件实例
 */
export async function initArrearsPlugin (config = {}) {
  arrearsLogger.info('==================== 初始化花瓣欠费组件 ====================')
  arrearsLogger.info('配置参数:', JSON.stringify(config, null, 2))

  try {
    // 加载插件
    arrearsLogger.info('步骤1: 加载插件 CDN...')
    await loadArrearsPlugin()

    if (!window.ArrearsPluginH5) {
      throw new Error('ArrearsPluginH5 未定义，CDN 加载可能失败')
    }

    arrearsLogger.info('步骤2: 创建插件实例...')

    // 初始化插件
    arrearsPlugin = new window.ArrearsPluginH5({
      appId: config.appId || arrearsAppId,
      openId: config.openId || '',
      parkingId: config.parkingId || '',
      isPaid: config.isPaid !== undefined ? config.isPaid : true,
      isDebug: config.isDebug !== undefined ? config.isDebug : true,
      callbackURL: config.callbackURL || ''
    })

    arrearsLogger.info('✅ 组件初始化成功')
    arrearsLogger.info('插件实例:', arrearsPlugin)
    arrearsLogger.info('插件方法:', Object.keys(arrearsPlugin))

    return arrearsPlugin
  } catch (err) {
    arrearsLogger.error('❌ 组件初始化失败', err)
    throw err
  }
}

/**
 * 查询车牌欠费数据
 * @param {string} plateNumber 车牌号
 * @param {number} carColor 车牌颜色 (LICENSE_CAR_COLOR 枚举值)
 * @param {Object} options 可选参数
 * @param {boolean} options.loading 是否显示loading
 * @param {string} options.actionMode 操作模式
 * @returns {Promise<Object>} 欠费数据
 */
export async function fetchArrearsOrder (plateNumber, carColor = LICENSE_CAR_COLOR.BLUE, options = {}) {
  arrearsLogger.info('==================== 查询欠费数据 ====================')
  arrearsLogger.info('请求参数:', { plateNumber, carColor, options })

  if (!arrearsPlugin) {
    arrearsLogger.warn('⚠️ 组件未初始化，请先调用 initArrearsPlugin')
    return null
  }

  try {
    arrearsLogger.info('调用 arrearsPlugin.fetchArrearsOrder...')
    arrearsLogger.info('插件实例类型:', typeof arrearsPlugin)
    arrearsLogger.info('插件原型:', Object.getPrototypeOf(arrearsPlugin))
    arrearsLogger.info('插件方法:', Object.getOwnPropertyNames(Object.getPrototypeOf(arrearsPlugin)))

    // 检查方法是否存在
    if (typeof arrearsPlugin.fetchArrearsOrder !== 'function') {
      arrearsLogger.error('❌ fetchArrearsOrder 方法不存在')
      arrearsLogger.info('插件所有属性:', Object.keys(arrearsPlugin))
      arrearsLogger.info('插件原型方法:', Object.getOwnPropertyNames(Object.getPrototypeOf(arrearsPlugin)))
      throw new Error('fetchArrearsOrder 方法不存在')
    }

    arrearsLogger.info('方法存在，开始调用...')

    // 调用查询方法
    const result = await arrearsPlugin.fetchArrearsOrder(plateNumber, carColor, {
      loading: options.loading !== undefined ? options.loading : false,
      actionMode: options.actionMode || 'DEFAULT'
    })

    arrearsLogger.info('✅ 查询成功')
    arrearsLogger.info('返回数据:', JSON.stringify(result, null, 2))

    return result
  } catch (err) {
    arrearsLogger.error('❌ 查询失败', err)
    arrearsLogger.error('错误类型:', err.constructor.name)
    arrearsLogger.error('错误信息:', err.message)
    arrearsLogger.error('错误堆栈:', err.stack)
    throw err
  }
}

/**
 * 获取插件实例
 * @returns {Object|null}
 */
export function getArrearsPlugin () {
  return arrearsPlugin
}

/**
 * 根据车牌号和cateID获取车牌颜色
 * @param {string} plateNumber 车牌号
 * @param {number} cateID 车辆类型ID
 * @returns {number} 车牌颜色枚举值
 */
export function getCarColor (plateNumber, cateID) {
  arrearsLogger.debug('获取车牌颜色:', { plateNumber, cateID })

  // 新能源车牌（8位）
  if (plateNumber && plateNumber.length === 8) {
    arrearsLogger.debug('新能源车牌，返回绿色')
    return LICENSE_CAR_COLOR.GREEN
  }

  arrearsLogger.debug('普通车牌，返回蓝色')
  return LICENSE_CAR_COLOR.BLUE
}
